import { indexAtOrAfter, indexAtOrBefore } from './timeBandOverlay';

/**
 * MOVING THE VIEWPORT: focusing a date range, and paging by a screenful.
 *
 * <h2>Pure functions over INDICES, because that is what the chart records</h2>
 *
 * `EChartComponent`'s own datazoom handler stores `startValue`/`endValue` and nothing else, so a
 * percentage dispatch moves the view and is then lost on the next option rebuild — a failure this
 * repository has already paid for twice, once in Elliott's "focus on the count" and once in the
 * replay's primed window. Everything here therefore resolves to integer bar indices on the axis
 * the chart is actually drawing.
 *
 * <h2>And they are pure, because the interesting decisions are all arithmetic</h2>
 *
 * Which bar a date lands on when it is a weekend; what a page is when the window is already at
 * the edge; what happens when a range falls entirely outside the series. None of that needs a
 * chart, and a test that drove one would be asserting against a fake.
 */

/** The calendar year as an inclusive `yyyy-mm-dd` pair. */
export function yearRange(year) {
  const y = String(year);
  if (!/^\d{4}$/.test(y)) return null;
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

/**
 * The index window for a date range, or null when it cannot be shown.
 *
 * <h2>The bounds SNAP INWARD, which is the same rule the session window follows</h2>
 *
 * A year starts on 1 January and ends on 31 December, and neither is reliably a trading day. The
 * start resolves to the first bar at or AFTER it and the end to the last bar at or BEFORE it, so
 * "2008" is the trading days inside 2008 and not a window that spills into the neighbouring
 * years. `frameBounds` resolves the session window the same way for the same reason.
 *
 * <h2>A missing bound runs to the series' own end</h2>
 *
 * Which is what the custom window already means by an absent date: unbounded on that side. A
 * reader who fills in only a start is asking for everything from there, and a focus that refused
 * would be the one control in the panel that read "unbounded" as "invalid".
 *
 * <p>Null when the range contains no bars at all — a window of zero width is not a view, and
 * dispatching one would collapse the chart onto a single column.
 */
export function focusWindow(dates, { from = null, to = null } = {}) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return null;
  const startValue = from ? indexAtOrAfter(axis, from) : 0;
  const endValue = to ? indexAtOrBefore(axis, to) : axis.length - 1;
  if (startValue < 0 || endValue < 0 || endValue < startValue) return null;
  return { startValue, endValue };
}

/**
 * The window shifted by one full screen, left or right.
 *
 * <h2>The SPAN is preserved exactly, including at the edges</h2>
 *
 * The brief's requirement, and it is the whole of the clamping decision: at the left edge the
 * window becomes `[0, span-1]` rather than `[0, end-shift]`, because truncating it would mean
 * paging into the start of the series quietly zoomed the chart in. A reader paging back through
 * a timeline is scrubbing at a fixed scale; a scale that changed when they reached the beginning
 * would read as a bug in the chart rather than as an edge.
 *
 * <p>A window WIDER than the series clamps to the whole series, which is the only honest answer:
 * the span cannot be preserved because it never fitted.
 *
 * @param window    `{startValue, endValue}` — the window now
 * @param direction -1 to page left, +1 to page right
 * @param total     how many bars the axis holds
 * @returns a new window, or null when there is nothing to move
 */
export function pageWindow(window, direction, total) {
  if (!window || !Number.isFinite(total) || total <= 0) return null;
  const { startValue, endValue } = window;
  if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) return null;
  const span = endValue - startValue + 1;
  if (span <= 0) return null;
  if (span >= total) return { startValue: 0, endValue: total - 1 };

  const step = direction < 0 ? -span : span;
  let start = startValue + step;
  if (start < 0) start = 0;
  if (start + span > total) start = total - span;
  const next = { startValue: start, endValue: start + span - 1 };
  // UNCHANGED means "already at that edge", and the caller is expected to skip the dispatch: a
  // no-op dispatch is one ECharts may decline to announce, which would leave the overlay's
  // claiming flag crediting a gesture that never happened.
  return next.startValue === startValue && next.endValue === endValue ? null : next;
}

/** Can this window page in that direction? For disabling a button rather than firing a no-op. */
export function canPage(window, direction, total) {
  return pageWindow(window, direction, total) !== null;
}

export default focusWindow;
